/**
 * Component: Product Colors Swatches
 * ------------------------------------------------------------------------------
 * Generates the color swatches based on the product handle and color tag.
 *
 * @namespace productSwatchesColors
 */
import axios from 'axios';
import removeAccents from 'remove-accents';
import {on} from '../helpers/utils';

/**
 * DOM selectors.
 */
const selectors = {
  quickviewContainer: '[js-quick-view="viewport"]',
  container: '[js-swatches-colors="container"]',
  colorName: '[js-swatches-colors="color-name"]',
};

/**
 * Create a new productSwatchesColors object.
 */
export default (swatchContainer) => {

  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: (swatchContainer === 'quickView')
      ? document.querySelector(selectors.quickviewContainer).querySelector(selectors.container)
      : document.querySelector(selectors.container),
    colorName: document.querySelector(selectors.colorName),
  };

  /**
   * Build the query for GraphQL from the product handle
   */
  function buildQuery() {
    const cleanHandle = nodeSelectors.container.dataset.handle;
    return `{ products(first:50, query:"${cleanHandle}*") { edges { node { id title handle tags productType variants(first:1) { edges { node { sku } } } } } } }`;
  }

  /**
   * GraphQL call for the products
   */
  function graphQlCall() {
    axios({
      url: '/api/2019-07/graphql.json',
      method: 'post',
      data: {
        query: buildQuery(),
      },
      headers: {
        'X-Shopify-Storefront-Access-Token': window.theme.token,
      },
    }).then((result) => {
      nodeSelectors.container.innerHTML = generateBlocks(result.data.data);
    }).catch((error) => {
      throw new Error('Error', error);
    });
  }

  /**
   * Generate the HTML code for the list of color variants
   * @param {Object} data - Axios call response
   */
  function generateBlocks(data) {
    let block = '';
    const assetsUrl = generateAssetsUrl();
    const currentSku = nodeSelectors.container.dataset.currentSku;

    data.products.edges.forEach((element) => {
      const variant = element.node.variants.edges[0].node;

      if (!variant.sku.includes(currentSku)) {
        return;
      }

      const color = element.node.tags.find((tag) => {
        return (tag.includes('parent_colour: '));
      });

      const colorName = color.replace('parent_colour: ', '').toLowerCase();
      const colorFileName = removeAccents(colorName.replace(/\s/g, '-').replace(/&/g, '_'));

      block += (colorName === nodeSelectors.container.dataset.currentColor)
      ? `<div class="swatch product-swatches-colors__swatch">
            <span
              class="product-swatches-colors__item product-swatches-colors__item--active swatch__button swatch-grid__item swatch"
              data-color="${colorName}"
              style="background-image:url('${assetsUrl.assetsFolder}color-${colorFileName}.png');"
            >${colorName}</span>
          </div>
        `
      : `<div class="swatch product-swatches-colors__swatch">
          <a
            href="/products/${element.node.handle}"
            class="product-swatches-colors__item swatch__button swatch-grid__item swatch"
            data-color="${colorName}"
            style="background-image:url('${assetsUrl.assetsFolder}color-${colorFileName}.png');"
          >${colorName}</a>
        </div>
        `;
    });
    return block;
  }

  function generateAssetsUrl() {
    const currentSwatch = nodeSelectors.container.dataset.currentSwatch;
    const filePath = currentSwatch.split('?');
    const fileName = filePath[0].substring(filePath[0].lastIndexOf('/') + 1);
    const newSwatchUrl = {
      assetsFolder: filePath[0].replace(fileName, ''),
      version: filePath[1],
    };
    return newSwatchUrl;
  }

  /**
   * Set the listeners
   * @param {HTMLElement} container - Swatches container
   */
  function setEventListeners(container) {
    on('mouseover', container, (event) => showColorName(event));
    on('mouseout', container, () => hideColorName());
  }

  /**
   * Show the colour name or remove it.
   * @param {Event} event - Mouseover event.
   */
  function showColorName(event) {
    const color = event.target.dataset.color;
    nodeSelectors.colorName.innerHTML = (color) ? color : '';
  }

  /**
   * Remove the colour name.
   */
  function hideColorName() {
    nodeSelectors.colorName.innerHTML = '';
  }

  /**
   * Initialise component.
   */
  function init() {
    graphQlCall();
    setEventListeners(nodeSelectors.container);
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  });
};
